import { IOptions } from 'common/reactSelectModels';
import { SwitchButton, SwitchButtonState } from 'components/SwitchButton/SwitchButton';
import { SaleOrderCustomer } from 'models/sale-order';
import React, { useContext, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import DatePicker from 'react-date-picker';
import Select from 'react-select';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/calendar.svg';
import './SaleOrderTransportForm.scss';

import moment from 'moment';
import { AuthenticationContext, IAuthProps } from 'services/context/authentication.context';

export const PAYMENT_DEADLINES = [
  { value: '0', label: 'Ettemaks' },
  { value: '3', label: '3 päeva' },
  { value: '5', label: '5 päeva' },
  { value: '7', label: '7 päeva' },
  { value: '10', label: '10 päeva' },
  { value: '14', label: '14 päeva' },
  { value: '21', label: '21 päeva' },
  { value: '30', label: '30 päeva' },
  { value: '45', label: '45 päeva' },
  { value: '60', label: '60 päeva' },
  { value: '90', label: '90 päeva' },
  { value: '120', label: '120 päeva' }
];

export interface TransportForm {
  validated?: boolean;
  invalid?: boolean;
  isOwnTransport: boolean;
  terminal: any;
  driver: any;
  car: any;
  carrier: any;
  dropoffLocation: any;
  date?: any;
  description: any;
  paymentDeadline?: any;
  notificationReceiverEmail: string | null;
}

export interface SaleOrderTransportFormProps {
  state: IAuthProps;
  form: TransportForm;
  carriers: IOptions[];
  cars: IOptions[];
  drivers: IOptions[];
  providers: IOptions[];
  customer?: SaleOrderCustomer;
  isLoading?: boolean;
  setForm: (form: TransportForm) => void;
}

export default function SaleOrderTransportForm({ form, setForm, carriers, cars, drivers, providers, customer, isLoading }: SaleOrderTransportFormProps) {
  const { state } = useContext(AuthenticationContext);

  return (<div>
    <Form className='d-flex flex-column' noValidate onSubmit={(e: any) => e.preventDefault()}>
      <div className='row mb-4'>
        <div className='col d-flex'>
          <SwitchButton
            className={"default-switch dark-switch" + (isLoading ? ' disabled' : '')}
            onActiveChange={evt => isLoading ? null : setForm({...form, isOwnTransport: evt === SwitchButtonState.LEFT})}
            labelLeft="Oma transport"
            labelRight="Terminal AS transport"
            active={form.isOwnTransport ? SwitchButtonState.LEFT : SwitchButtonState.RIGHT}
          />
        </div>
      </div>
      {
        form.isOwnTransport ?
        <OwnTransportForm state={state} form={form} setForm={setForm} carriers={carriers} cars={cars} drivers={drivers} providers={providers} customer={customer} isLoading={isLoading} /> :
        <TerminalTransportForm state={state} customer={customer} form={form} setForm={setForm} />
      }
    </Form>
  </div>)
}

function OwnTransportForm({ state, form, setForm, carriers, cars, drivers, providers, isLoading, customer }: SaleOrderTransportFormProps) {

  const MIN_SEARCH_TERM_LENGTH = 3;
  const [filteredCarriers, setFilteredCarriers] = useState(carriers);
  const [filteredCars, setFilteredCars] = useState(cars);
  const [filteredDrivers, setFilteredDrivers] = useState(drivers);

  useEffect(() => {
    setFilteredCarriers([]);
  }, [carriers]);

  useEffect(() => {
    setFilteredCars([]);
    setFilteredDrivers([]);
  }, [cars, drivers]);

  function filterCarriers(value: string) {
    if (!value || value.length < MIN_SEARCH_TERM_LENGTH) {
      setFilteredCarriers([]);
    } else {
      setFilteredCarriers(carriers.filter(c => c.label.toLocaleLowerCase().indexOf(value.toLocaleLowerCase()) !== -1));
    }
  }
  function filterCars(value: string) {
    if (!value || !value.length) {
      setFilteredCars([]);
    } else {
      setFilteredCars(cars.filter(c => c.label.toLocaleLowerCase().indexOf(value.toLocaleLowerCase()) !== -1));
    }
  }
  function filterDrivers(value: string) {
    if (!value || !value.length) {
      setFilteredDrivers([]);
    } else {
      setFilteredDrivers(drivers.filter(c => c.label.toLocaleLowerCase().indexOf(value.toLocaleLowerCase()) !== -1));
    }
  }

  const locations = customer?.locations.filter(l => l.mainAddress).map(l => ({ value: l.customerLocationId + '', label: l.mainAddress + (l.city ? ', ' + l.city : '') })) || [];

  return (
    <>
      <div className='row'>
        <div className='col'>
          <Form.Group controlId={'form-providers'}>
              <Form.Label>
                  <span className='text-nowrap'>Terminal</span>
              </Form.Label>
              <Select
                  classNamePrefix={'select'}
                  value={providers.find(provider => provider.value === form.terminal?.value)}
                  options={providers}
                  className={'select-terminal ' + (!form.terminal && form.validated ? 'invalid' : '')}
                  placeholder='Vali terminal…'
                  isClearable={false}
                  autosize={true}
                  container={'body'}
                  noOptionsMessage={() => "Vasted puuduvad"}
                  isLoading={isLoading}
                  loadingMessage={() => 'Laen andmeid...'}
                  required
                  onChange={(option) =>
                      setForm({
                        ...form,
                        terminal: option
                      })}
              />
              {
                  !form.terminal && form.validated &&
                  <div className="d-block text-right invalid-feedback">Kohustuslik väli</div>
              }
          </Form.Group>
        </div>
        <div className='col'>
          <Form.Group controlId={'form-carriers'}>
              <Form.Label>
                  <span className='text-nowrap'>Vedaja</span>
              </Form.Label>
              <Select
                  value={carriers.find(carrier => carrier.value === form.carrier?.value)}
                  options={filteredCarriers}
                  className={'select-terminal '
                  + (!form.carrier && form.validated ? 'invalid' : '')}
                  placeholder='Vali vedaja…'
                  classNamePrefix={'select'}
                  isClearable={false}
                  autosize={true}
                  container={'body'}
                  noOptionsMessage={() => carriers.length ? 'Otsimiseks trüki min 3 tähte' : 'Vasted puuduvad'}
                  isLoading={isLoading}
                  loadingMessage={() => 'Laen andmeid...'}
                  onInputChange={filterCarriers}
                  required
                  onChange={(option) =>
                      setForm({
                            ...form,
                            carrier: option
                      })}
              />
              {
                  !form.carrier && form.validated &&
                  <div className="d-block text-right invalid-feedback">Kohustuslik väli</div>
              }
          </Form.Group>
        </div>
      </div>
      <div className='row'>
        <div className='col'>
          <Form.Group controlId={'form-drivers'}>
              <Form.Label>
                  <span className='text-nowrap'>Autojuht</span>
              </Form.Label>
              <Select
                  classNamePrefix={'select'}
                  value={drivers.find(driver => driver.value === form.driver?.value) || null}
                  options={filteredDrivers}
                  className={'select-terminal ' + (!form.driver && form.validated ? 'invalid' : '')}
                  placeholder='Vali juht…'
                  isClearable={false}
                  isDisabled={!form.carrier?.value}
                  autosize={true}
                  container={'body'}
                  noOptionsMessage={() => carriers.length ? 'Sisesta sõitja nimi' : 'Vasted puuduvad'}
                  isLoading={isLoading}
                  loadingMessage={() => 'Laen andmeid...'}
                  onInputChange={filterDrivers}
                  onChange={(option) =>
                      setForm({
                        ...form,
                        driver: option
                      })}
              />
              {
                  !form.driver && form.validated &&
                  <div className="d-block text-right invalid-feedback">Kohustuslik väli</div>
              }
          </Form.Group>
        </div>
        <div className='col'>
          <Form.Group controlId={'form-cars'}>
              <Form.Label>
                  <span className='text-nowrap'>Veovahend</span>
              </Form.Label>
              <Select
                  value={cars.find(car => car.value === form.car?.value) || null}
                  options={filteredCars}
                  className={'select-terminal '
                  + (!form.car && form.validated ? 'invalid' : '')}
                  placeholder='Vali auto…'
                  classNamePrefix={'select'}
                  isClearable={false}
                  isDisabled={!form.carrier?.value}
                  autosize={true}
                  noOptionsMessage={() => carriers.length ? 'Sisesta sõiduki number' : 'Vasted puuduvad'}
                  container={'body'}
                  isLoading={isLoading}
                  loadingMessage={() => 'Laen andmeid...'}
                  onInputChange={filterCars}
                  required
                  onChange={(option) => {
                      return setForm({
                        ...form,
                        car: option
                      });
                  }}
              />
              {
                  !form.car && form.validated &&
                  <div className="d-block text-right invalid-feedback">Kohustuslik väli</div>
              }
          </Form.Group>
        </div>
      </div>
      <div className='row'>
        <div className='col-6'>
          <DatePickerFormGroup form={form} setForm={setForm} />
        </div>
        <div className='col'>
          <Form.Group controlId={'form-dropoff-location'}>
              <Form.Label>
                  <span className='text-nowrap'>Mahalaadimiskoht</span>
              </Form.Label>
              <Select
                  classNamePrefix={'select'}
                  value={locations.find(l => l.value === form.dropoffLocation?.value)}
                  options={locations}
                  className={'select-terminal ' + (!form.dropoffLocation && form.validated ? 'invalid' : '')}
                  placeholder='Vali asukoht…'
                  isClearable={false}
                  autosize={true}
                  container={'body'}
                  noOptionsMessage={() => "Vasted puuduvad"}
                  isLoading={isLoading}
                  loadingMessage={() => 'Laen andmeid...'}
                  required
                  onChange={(option) =>
                      setForm({
                        ...form,
                        dropoffLocation: option
                      })}
              />
              {
                  !form.dropoffLocation && form.validated &&
                  <div className="d-block text-right invalid-feedback">Kohustuslik väli</div>
              }
          </Form.Group>
        </div>        
      </div>
      <div className='row'>
        <div className='col-12 col-lg-6'>
          <Form.Label>
              <span className='text-nowrap'>Maksetähtaeg</span>
          </Form.Label>
          <Select
            classNamePrefix="select"
            placeholder='Vali maksetähtaeg…'
            value={PAYMENT_DEADLINES.find(d => d.value === form.paymentDeadline?.value)}
            required
            onChange={(option: any) =>
              setForm({
                ...form,
                paymentDeadline: option
              })}
            className={'select-terminal ' + (!form.paymentDeadline && form.validated ? 'invalid' : '')}
            options={PAYMENT_DEADLINES.filter(d => state.customer.extendedSaleOrderDeadlineAllowed || parseInt(d.value) <= state.customer.defaultPaymentDeadline || 0)}
          />
          {
              !form.paymentDeadline && form.validated &&
              <div className="d-block text-right invalid-feedback">Kohustuslik väli</div>
          }
        </div>
      </div>
      <div className='row mt-3'>
        <div className='col'>
          <TextareaFormGroup form={form} setForm={setForm}/>
        </div>
      </div>
    </>  
  );
}

function TerminalTransportForm({ state, customer, form, setForm }: { state: IAuthProps, customer?: SaleOrderCustomer, setForm: (form: TransportForm) => void, form: TransportForm}) {

  if (!customer) {
    return null;
  }
  const locations = customer.locations.filter(l => l.mainAddress).map(l => ({ value: l.customerLocationId + '', label: l.mainAddress + (l.city ? ', ' + l.city : '') }));

  return (
    <>
      <div className='row'>
        <div className='col'>
          <DatePickerFormGroup form={form} setForm={setForm} />
        </div>        
        <div className='col'>
          <Form.Group controlId={'form-dropoff-location'}>
              <Form.Label>
                  <span className='text-nowrap'>Mahalaadimiskoht</span>
              </Form.Label>
              <Select
                  classNamePrefix={'select'}
                  value={locations.find(l => l.value === form.dropoffLocation?.value)}
                  options={locations}
                  className={'select-terminal ' + (!form.dropoffLocation && form.validated ? 'invalid' : '')}
                  placeholder='Vali asukoht…'
                  isClearable={false}
                  autosize={true}
                  container={'body'}
                  noOptionsMessage={() => "Vasted puuduvad"}
                  onChange={(option) =>
                      setForm({
                        ...form,
                        dropoffLocation: option
                      })}
              />
              {
                  !form.dropoffLocation && form.validated &&
                  <div className="d-block text-right invalid-feedback">Kohustuslik väli</div>
              }
          </Form.Group>
        </div>
      </div>
      <div className='row'>
        <div className='col-12 col-lg-6'>
          <Form.Label>
              <span className='text-nowrap'>Maksetähtaeg</span>
          </Form.Label>
          <Select
            classNamePrefix="select"
            placeholder='Vali maksetähtaeg…'
            value={PAYMENT_DEADLINES.find(d => d.value === form.paymentDeadline?.value)}
            required
            onChange={(option: any) =>
              setForm({
                ...form,
                paymentDeadline: option
              })}
            className={'select-terminal ' + (!form.paymentDeadline && form.validated ? 'invalid' : '')}
            options={PAYMENT_DEADLINES.filter(d => state.customer.extendedSaleOrderDeadlineAllowed || parseInt(d.value) <= (state.customer.defaultPaymentDeadline || 0))}
          />
          {
              !form.paymentDeadline && form.validated &&
              <div className="d-block text-right invalid-feedback">Kohustuslik väli</div>
          }
        </div>
      </div>
      <div className='row mt-3'>
        <div className='col'>
          <TextareaFormGroup form={form} setForm={setForm}/>
        </div>
      </div>
    </>
  );
}

function TextareaFormGroup({ form, setForm }: { form: TransportForm, setForm: (form: TransportForm) => void }){
  const [characterCount, setCharacterCount] = useState(0)

  useEffect(() => {
    if(form.description){
      setCharacterCount(form.description.length)
    }else{
      setCharacterCount(0);
    }
  }, [form.description])

  return (
    <Form.Group controlId='form-desc'>
      <Form.Label>
        <span className='text-nowrap'>Muu info</span>
      </Form.Label>
      <div>
        <Form.Control
          as="textarea"
          value={form.description}
          type="text"
          autoComplete="off"
          onChange={(e) => setForm({
            ...form,
            description: e.target.value
          })}
          className={"textarea-terminal " + (form.description && form.description.length > 250 ? 'invalid' : '')}
        />
        <div className='d-flex justify-between'>
          <span className='mt-1 text-gray-700'>{characterCount}/250</span>
          {
              form.description && form.description.length > 250 &&
              <div className="d-block text-right invalid-feedback">Info väli ei tohi ületada 250 tähemärki</div>
          }
        </div>
      </div>
    </Form.Group>
  ) 
}

function DatePickerFormGroup({ form, setForm }: { form: TransportForm, setForm: (form: TransportForm) => void }) {
  const isBefore = moment().set({hour: 16, minute: 0}) > moment();
  const minDate = form.isOwnTransport && isBefore ? moment().toDate() : moment().add(1, 'day').toDate();

  return (
    <Form.Group controlId={'form-date'} className='d-flex flex-column'>
        <Form.Label>
            <span className='text-nowrap'>Tarneaeg</span>
        </Form.Label>
        <DatePicker
          className={!form.date && form.validated ? 'invalid' : ''}
          value={form.date}
          locale='et'
          clearIcon={null}
          calendarIcon={<CalendarIcon />}
          format='dd.MM.yyyy'
          minDate={minDate}
          required
          onChange={(event: any) => {
            setForm({
              ...form, date: event ? new Date(event) : undefined
            })
          }}
        />
        {
            !form.date && form.validated &&
            <div className="d-block text-right invalid-feedback">Kohustuslik väli</div>
        }
    </Form.Group> 
  )
}